import { useRouter } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import { Platform, ScrollView, Share, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { GhostButton, PrimaryButton } from '@/components/buttons';
import { PillarBar } from '@/components/pillar-bar';
import { useGame } from '@/game/game-context';
import { getPerson } from '@/game/people';
import { maybeRequestReview } from '@/game/review-prompt';
import { PILLAR_LABELS, formatValuation, simulate } from '@/game/scoring';
import { isComplete } from '@/game/session';
import { buildShareText } from '@/game/share';
import { CORE_PILLARS, ROLES, ROLE_LABELS } from '@/game/types';
import { MAX_WIDTH, colors, radius, type } from '@/theme';

const SIM_LINES = [
  'INCORPORATING',
  'RAISING THE SEED ROUND',
  'SHIPPING V1',
  'HIRING 400 PEOPLE',
  'SERIES B, OVERSUBSCRIBED',
  'A COFOUNDER IS LEAVING',
  'SCALING TO 50 COUNTRIES',
  'THE BOARD IS MEETING',
  'RUNNING THE NUMBERS',
];

const SIM_DURATION = 2200;
const LINE_INTERVAL = SIM_DURATION / SIM_LINES.length;

/** Long enough for the fade to /draft to land before the review sheet opens. */
const REVIEW_PROMPT_DELAY_MS = 900;

export default function ResultScreen() {
  const router = useRouter();
  const { state, start } = useGame();
  const complete = isComplete(state);

  const result = useMemo(() => simulate(state.roster), [state.roster]);
  const rare = result.tier === 'trillion';
  const grim = result.tier === 'dead' || result.tier === 'imploded';

  const [phase, setPhase] = useState(0);
  const [line, setLine] = useState(0);
  const [shownValuation, setShownValuation] = useState(0);
  const [copied, setCopied] = useState(false);

  // A half-finished roster can only happen via a reload — bounce to the title.
  // `leaving` keeps that guard from hijacking the deliberate reset in Play again.
  const leaving = useRef(false);
  useEffect(() => {
    if (!complete && !leaving.current) router.replace('/');
  }, [complete, router]);

  useEffect(() => {
    const ticker = setInterval(() => setLine((n) => Math.min(n + 1, SIM_LINES.length - 1)), LINE_INTERVAL);
    const timers = [
      setTimeout(() => setPhase(1), SIM_DURATION),
      setTimeout(() => setPhase(2), SIM_DURATION + 900),
      setTimeout(() => setPhase(3), SIM_DURATION + 1900),
    ];
    return () => {
      clearInterval(ticker);
      timers.forEach(clearTimeout);
    };
  }, []);

  // Valuation ticks up rather than just appearing — the money is the punchline.
  useEffect(() => {
    if (phase < 1) return;
    const startedAt = Date.now();
    const duration = 1500;
    let frame = 0;
    const tick = () => {
      const t = Math.min(1, (Date.now() - startedAt) / duration);
      const eased = 1 - (1 - t) ** 3;
      setShownValuation(result.valuation * eased);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [phase, result.valuation]);

  const onShare = async () => {
    const message = buildShareText(state.roster, result);
    if (Platform.OS === 'web') {
      await navigator.clipboard?.writeText(message).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }
    try {
      await Share.share({ message });
    } catch {
      // The user dismissed the share sheet — nothing to do.
    }
  };

  const onPlayAgain = () => {
    leaving.current = true;
    start();
    router.replace('/draft');
    // They finished a run and came back for another — the one moment worth
    // spending the single review ask on. Delayed past the screen fade so the
    // sheet isn't presented into a transition and dropped.
    setTimeout(maybeRequestReview, REVIEW_PROMPT_DELAY_MS);
  };

  if (!complete) return <View style={styles.safe} />;

  if (phase === 0) {
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.simulating}>
          <Text style={styles.simLabel}>SIMULATING</Text>
          <Text style={styles.simLine}>{SIM_LINES[line]}</Text>
          <View style={styles.simTrack}>
            <View style={[styles.simFill, { width: `${((line + 1) / SIM_LINES.length) * 100}%` }]} />
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.container}>
          <Animated.View entering={FadeInDown.duration(420)} style={styles.outcome}>
            <Text style={styles.kicker}>OUTCOME</Text>
            <Text style={[styles.headline, grim && styles.headlineGrim]}>{result.headline}</Text>
            <Text style={[styles.valuation, grim && styles.valuationGrim]}>
              {formatValuation(shownValuation)} valuation
            </Text>
            {rare && (
              <Animated.Text entering={FadeIn.delay(600).duration(400)} style={styles.rareBadge}>
                ★ ABOUT 1 IN 100 DRAFTS GET HERE
              </Animated.Text>
            )}
          </Animated.View>

          <Animated.View entering={FadeIn.delay(300).duration(400)} style={styles.summary}>
            {result.summary.map((sentence) => (
              <Text key={sentence} style={styles.summaryLine}>
                {sentence}
              </Text>
            ))}
          </Animated.View>

          {phase >= 2 && (
            <View style={styles.pillars}>
              {CORE_PILLARS.map((pillar, index) => (
                <PillarBar
                  key={pillar}
                  label={PILLAR_LABELS[pillar]}
                  value={result.scores[pillar]}
                  delay={index * 90}
                />
              ))}
              <PillarBar
                label={PILLAR_LABELS.chaos}
                value={result.scores.chaos}
                delay={CORE_PILLARS.length * 90}
                danger
              />
            </View>
          )}

          {phase >= 3 && (
            <Animated.View entering={FadeInDown.duration(420)} style={styles.cardWrap}>
              <View style={[styles.card, rare && styles.cardRare]}>
                <Text style={styles.cardTitle}>MY 1-T COMPANY</Text>
                <View style={styles.cardRows}>
                  {ROLES.map((role) => {
                    const pick = state.roster[role];
                    return (
                      <View key={role} style={styles.cardRow}>
                        <Text style={styles.cardRole}>{ROLE_LABELS[role].toUpperCase()}</Text>
                        <Text style={styles.cardName} numberOfLines={1}>
                          {pick ? getPerson(pick.personId).name : '—'}
                        </Text>
                      </View>
                    );
                  })}
                </View>
                <View style={styles.divider} />
                <View style={styles.cardStats}>
                  <View style={styles.cardStat}>
                    <Text style={styles.cardStatLabel}>VALUATION</Text>
                    <Text style={styles.cardStatValue}>{formatValuation(result.valuation)}</Text>
                  </View>
                  <View style={styles.cardStat}>
                    <Text style={styles.cardStatLabel}>CHAOS</Text>
                    <Text style={[styles.cardStatValue, styles.cardStatChaos]}>
                      {result.scores.chaos}/100
                    </Text>
                  </View>
                </View>
              </View>

              <PrimaryButton
                label={copied ? 'COPIED TO CLIPBOARD' : 'SHARE MY COMPANY'}
                onPress={onShare}
              />
              <GhostButton label="Play again" onPress={onPlayAgain} />
            </Animated.View>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  scroll: {
    flexGrow: 1,
  },
  container: {
    width: '100%',
    maxWidth: MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 32,
    gap: 26,
  },
  simulating: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 14,
  },
  simLabel: {
    ...type.label,
    color: colors.faint,
  },
  simLine: {
    ...type.title,
    fontSize: 22,
    color: colors.text,
    textAlign: 'center',
  },
  simTrack: {
    width: 160,
    height: 3,
    borderRadius: radius.pill,
    backgroundColor: colors.cardHi,
    overflow: 'hidden',
    marginTop: 8,
  },
  simFill: {
    height: '100%',
    backgroundColor: colors.gold,
  },
  outcome: {
    alignItems: 'center',
    gap: 6,
    paddingTop: 12,
  },
  kicker: {
    ...type.label,
    color: colors.dim,
  },
  headline: {
    ...type.display,
    fontSize: 40,
    lineHeight: 44,
    color: colors.text,
    textAlign: 'center',
  },
  headlineGrim: {
    color: colors.dim,
  },
  valuation: {
    ...type.title,
    fontSize: 30,
    color: colors.gold,
    marginTop: 4,
  },
  valuationGrim: {
    color: colors.red,
  },
  rareBadge: {
    ...type.label,
    fontSize: 10,
    color: colors.gold,
    marginTop: 10,
  },
  summary: {
    alignItems: 'center',
    gap: 3,
  },
  summaryLine: {
    ...type.body,
    fontSize: 16,
    lineHeight: 23,
    color: colors.dim,
    textAlign: 'center',
  },
  pillars: {
    gap: 14,
  },
  cardWrap: {
    gap: 12,
  },
  card: {
    backgroundColor: colors.card,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 22,
    gap: 16,
    marginBottom: 8,
  },
  cardRare: {
    borderColor: colors.gold,
  },
  cardTitle: {
    ...type.label,
    fontSize: 12,
    color: colors.dim,
  },
  cardRows: {
    gap: 10,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  cardRole: {
    ...type.label,
    fontSize: 10,
    color: colors.faint,
  },
  cardName: {
    ...type.title,
    fontSize: 17,
    color: colors.text,
    flexShrink: 1,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border,
  },
  cardStats: {
    flexDirection: 'row',
    gap: 32,
  },
  cardStat: {
    gap: 3,
  },
  cardStatLabel: {
    ...type.label,
    fontSize: 10,
    color: colors.faint,
  },
  cardStatValue: {
    ...type.title,
    fontSize: 24,
    color: colors.gold,
  },
  cardStatChaos: {
    color: colors.red,
  },
});
